/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View, Text, TextField, ErrorText, Pressable } from '@components/atoms'
import { Modal, ModalInfoType } from '@components/molecules'
import {
	Select,
	SelectChangeEvent,
	FormControl,
	MenuItem,
	InputLabel,
} from '@mui/material'
import DaumPostcode from 'react-daum-postcode'

import {
	useAppSelector,
	useAppDispatch,
	paymentActions,
	PaymentDeliveryType,
	PaymentStateType,
} from '@redux/index'
import { unsupportedPayPalCountries } from '@redux/store/home/payment.store'
import { produce } from 'immer'
import { grey, deepPurple } from '@mui/material/colors'
import { Country, ICountry, State, IState } from 'country-state-city'

export const PaymentDelivery = () => {
	// redux dispatch
	const reduxDispatch = useAppDispatch()

	// redux state
	const paymentMethod: string = useAppSelector(state => state.payment.paymentMethod)
	const delivery: PaymentDeliveryType = useAppSelector(
		state => state.payment.paymentDelivery,
	)
	const errorMessages: PaymentStateType['paymentErrorMessages'] = useAppSelector(
		state => state.payment.paymentErrorMessages,
	)

	// state
	const [modalInfo, setModalInfo] = React.useState<ModalInfoType>({
		target: '',
		title: '',
	})
	const [showModal, setShowModal] = React.useState<boolean>(false)

	const [searchTerm, setSearchTerm] = React.useState('')
	const searchTimeout = React.useRef<NodeJS.Timeout | null>(null)
	const menuItemRefs = React.useRef<Array<HTMLLIElement | null>>([])

	const countries: ICountry[] = React.useMemo(() => {
		const res: ICountry[] = Country.getAllCountries()
		res.sort((a, b) => {
			if (a.name > b.name) {
				return 1
			} else if (a.name < b.name) {
				return -1
			}

			return 0
		})
		return res
	}, [])

	const states: IState[] = React.useMemo(() => {
		const res: IState[] =
			delivery.countryIsoCode === ''
				? []
				: State.getStatesOfCountry(delivery.countryIsoCode)

		return res
	}, [delivery.countryIsoCode])

	const onCountryChange = (e: SelectChangeEvent) => {
		const countryName = e.target.value
		const thisCountry = countries.filter(aCountry => aCountry.name === countryName)[0]
		const newDelivery = produce(delivery, draft => {
			draft.countryName = countryName
			draft.countryIsoCode = thisCountry.isoCode
			draft.stateName = ''
			draft.stateIsoCode = ''
			draft.city = ''
			draft.street1 = ''
			draft.zip = ''
		})

		if (
			paymentMethod === 'paypal' &&
			unsupportedPayPalCountries.includes(thisCountry.isoCode)
		) {
			reduxDispatch(paymentActions.PAYMENT_METHOD('credit'))

			reduxDispatch(
				paymentActions.MODAL_INFO({
					target: 'paypalNotAllowed',
					title: 'PayPal Unavailable',
				}),
			)
			reduxDispatch(paymentActions.SHOW_MODAL(true))
		}

		reduxDispatch(
			paymentActions.PAYMENT_DELIVERY({
				...newDelivery,
			}),
		)
	}

	const onStateSelectChange = (e: SelectChangeEvent) => {
		const stateName = e.target.value
		const thisState = states.filter(aState => aState.name === stateName)[0]
		const newDelivery = produce(delivery, draft => {
			draft.stateName = stateName
			draft.stateIsoCode = thisState.isoCode
		})

		reduxDispatch(
			paymentActions.PAYMENT_DELIVERY({
				...newDelivery,
			}),
		)
	}

	const onStateTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const str = e.target.value

		const newDelivery = produce(delivery, draft => {
			draft.stateName = str
		})

		reduxDispatch(
			paymentActions.PAYMENT_DELIVERY({
				...newDelivery,
			}),
		)
	}

	const onStreet1Change = (e: React.ChangeEvent<HTMLInputElement>) => {
		const str = e.target.value

		const newDelivery = produce(delivery, draft => {
			draft.street1 = str
		})

		reduxDispatch(
			paymentActions.PAYMENT_DELIVERY({
				...newDelivery,
			}),
		)
	}
	const onStreet2Change = (e: React.ChangeEvent<HTMLInputElement>) => {
		const str = e.target.value

		const newDelivery = produce(delivery, draft => {
			draft.street2 = str
		})

		reduxDispatch(
			paymentActions.PAYMENT_DELIVERY({
				...newDelivery,
			}),
		)
	}
	const onCityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const str = e.target.value

		const newDelivery = produce(delivery, draft => {
			draft.city = str
		})

		reduxDispatch(
			paymentActions.PAYMENT_DELIVERY({
				...newDelivery,
			}),
		)
	}

	const onZipChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const str = e.target.value

		const newDelivery = produce(delivery, draft => {
			draft.zip = str
		})

		reduxDispatch(
			paymentActions.PAYMENT_DELIVERY({
				...newDelivery,
			}),
		)
	}

	const handleComplete = (data: any) => {
		setShowModal(false)

		console.log(data)

		const roadAddressArr = data.roadAddress.split(`${data.sido} ${data.sigungu} `)
		const roadAddress = roadAddressArr.length > 1 ? roadAddressArr[1] : roadAddressArr[0]

		const newDelivery = produce(delivery, draft => {
			draft.zip = data.zonecode
			draft.stateIsoCode = data.sidoEnglish
			draft.stateName = data.sido
			draft.city = data.sigungu
			draft.street1 = roadAddress
		})

		reduxDispatch(
			paymentActions.PAYMENT_DELIVERY({
				...newDelivery,
			}),
		)
	}

	const onKrAddressAutoCompleteClick = () => {
		setModalInfo({
			target: 'daumAddress',
			title: '주소 검색',
		})
		setShowModal(true)
	}

	const onModalSubmit = () => {}

	const handleCountryKeyDown = (event: React.KeyboardEvent) => {
		const key = event.key

		if (/^[a-zA-Z0-9]$/.test(key)) {
			// 알파벳 혹은 숫자만 처리
			if (searchTimeout.current) {
				clearTimeout(searchTimeout.current)
			}
			const newTerm = searchTerm + key.toLowerCase()
			setSearchTerm(newTerm)
			searchTimeout.current = setTimeout(() => setSearchTerm(''), 500)

			// newTerm와 일치하는 항목 찾기
			const matchedIndex = countries.findIndex(country =>
				country.name.toLowerCase().startsWith(newTerm),
			)

			if (matchedIndex !== -1) {
				const item = menuItemRefs.current[matchedIndex]
				if (item) {
					// 해당 MenuItem에 포커스 이동
					item.focus()
					// 스크롤이 필요한 경우를 위해 부드럽게 스크롤 처리
					item.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
				}
			}
		}
	}

	return (
		<View css={style} className="infoView">
			<View className="infoTitleView">
				<Text category="h3">Delivery</Text>
			</View>

			<View className="infoInnerView delivery">
				<View className="inputView">
					<FormControl fullWidth>
						<InputLabel
							id="delivery-country-select-label"
							error={errorMessages.delivery.countryName !== ''}
						>
							Country *
						</InputLabel>
						<Select
							labelId="delivery-country-select-label"
							id="delivery-country-select"
							name="delivery-country-select"
							label="Country *"
							value={delivery.countryName}
							size="small"
							error={errorMessages.delivery.countryName !== ''}
							style={{ minHeight: '3.5rem', textAlign: 'left' }}
							onChange={onCountryChange}
							MenuProps={{
								MenuListProps: {
									onKeyDown: handleCountryKeyDown,
								},
							}}
						>
							{countries.map((aCountry: ICountry, index: number) => {
								return (
									<MenuItem
										key={aCountry.isoCode}
										sx={{
											'&.Mui-focusVisible': {
												background: grey[200],
											},
										}}
										value={aCountry.name}
										ref={el => (menuItemRefs.current[index] = el)}
									>
										<Text category="p1" css={{ paddingRight: '0.8rem' }}>
											{`${aCountry.flag} ${aCountry.name}`}
										</Text>
									</MenuItem>
								)
							})}
						</Select>

						{errorMessages.delivery.countryName !== '' && (
							<ErrorText className="errorMsg">
								{errorMessages.delivery.countryName}
							</ErrorText>
						)}
					</FormControl>
				</View>

				{delivery.countryIsoCode === 'KR' ? (
					<>
						<View className="inputView">
							<Pressable
								className="searchAddressButton"
								onClick={onKrAddressAutoCompleteClick}
							>
								<TextField
									type="text"
									label="주소 *"
									value={`${delivery.stateName} ${delivery.city} ${delivery.street1}`}
									InputProps={{
										readOnly: true,
									}}
								/>
							</Pressable>
						</View>

						<View className="inputView">
							<TextField
								type="text"
								label="상세 주소"
								onChange={onStreet2Change}
								value={delivery.street2}
							/>
						</View>

						<View className="inputView">
							<TextField
								type="text"
								label="우편번호"
								value={delivery.zip}
								InputProps={{
									readOnly: true,
								}}
							/>
						</View>
					</>
				) : (
					<>
						<View className="inputView">
							<TextField
								type="text"
								label="ZIP code (Postal Code) *"
								onChange={onZipChange}
								value={delivery.zip}
								error={errorMessages.delivery.zip !== ''}
							/>

							{errorMessages.delivery.zip !== '' && (
								<ErrorText className="errorMsg">{errorMessages.delivery.zip}</ErrorText>
							)}
						</View>

						<View className="inputView">
							<TextField
								type="text"
								label="Street1 *"
								error={errorMessages.delivery.street1 !== ''}
								onChange={onStreet1Change}
								value={delivery.street1}
							/>
							{errorMessages.delivery.street1 !== '' && (
								<ErrorText className="errorMsg">
									{errorMessages.delivery.street1}
								</ErrorText>
							)}
						</View>
						<View className="inputView">
							<TextField
								type="text"
								label="Street2"
								onChange={onStreet2Change}
								value={delivery.street2}
							/>
						</View>
						<View className="inputView">
							<TextField
								type="text"
								label="City *"
								error={errorMessages.delivery.city !== ''}
								onChange={onCityChange}
								value={delivery.city}
							/>
							{errorMessages.delivery.city !== '' && (
								<ErrorText className="errorMsg">{errorMessages.delivery.city}</ErrorText>
							)}
						</View>
						<View className="inputView">
							{states.length === 0 ? (
								<TextField
									type="text"
									label="State / Province / Region *"
									onChange={onStateTextChange}
									value={delivery.stateName}
									error={errorMessages.delivery.states !== ''}
								/>
							) : (
								<FormControl fullWidth>
									<InputLabel
										id="delivery-state-select-label"
										error={errorMessages.delivery.countryName !== ''}
									>
										State / Province / Region *
									</InputLabel>
									<Select
										labelId="delivery-state-select-label"
										id="delivery-state-select"
										name="delivery-state-select"
										label="State / Province / Region *"
										value={delivery.stateName}
										size="small"
										error={errorMessages.delivery.states !== ''}
										style={{ minHeight: '3.5rem', textAlign: 'left' }}
										onChange={onStateSelectChange}
									>
										{states.map((aState: IState) => {
											return (
												<MenuItem
													key={aState.isoCode}
													sx={{
														'&.Mui-focusVisible': {
															background: grey[200],
														},
													}}
													value={aState.name}
												>
													<Text category="p1" css={{ paddingRight: '0.8rem' }}>
														{aState.name}
													</Text>
												</MenuItem>
											)
										})}
									</Select>

									{errorMessages.delivery.countryName !== '' && (
										<ErrorText className="errorMsg">
											{errorMessages.delivery.countryName}
										</ErrorText>
									)}
								</FormControl>
							)}
							{errorMessages.delivery.states !== '' && (
								<ErrorText className="errorMsg">
									{errorMessages.delivery.states}
								</ErrorText>
							)}
						</View>
					</>
				)}
			</View>

			<Modal
				target={modalInfo.target}
				title={modalInfo.title}
				show={showModal}
				onCloseModal={() => {
					setShowModal(false)
				}}
				closeStatus="inherit"
				showSubmit={false}
				showClose={false}
				submitIconColor={deepPurple['A400']}
				onSubmit={onModalSubmit}
				rootStyle={{
					maxWidth: '70rem',
				}}
			>
				<View css={{ width: '68rem' }}>
					<DaumPostcode onComplete={handleComplete} />
				</View>
			</Modal>
		</View>
	)
}

const style = css({})
